import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";

/**
 * s228 (D-228.70, Reade; board v351) — THE CHAPTER'S STEP PILLS: all on ONE line when there is room; when they must wrap,
 * BALANCED rows — 5 and 4, never 8 and 1 — so no pill is left alone on a line (an orphan).
 *
 * The browser's flex-wrap fills a line and drops the rest, which is how nine pills became seven and two. So the pills
 * are measured (each is `whitespace-nowrap`, so its width does not depend on the layout) and split here:
 *   • if they fit on the row after Back and the chapter label, they stay there, inline;
 *   • otherwise they take their own full-width line(s) below, split into the FEWEST rows that fit, as evenly as
 *     possible (row sizes differ by at most one, the longer rows first).
 */

export const PILL_GAP = 8;

/** Row sizes for `widths` in `available` px: `[n]` when one row fits; else the fewest balanced rows that fit. Pure. */
export function balancedRows(widths: readonly number[], available: number, gap = PILL_GAP): number[] {
  const n = widths.length;
  if (n === 0) return [];
  const rowWidth = (from: number, count: number) =>
    widths.slice(from, from + count).reduce((a, b) => a + b, 0) + gap * Math.max(count - 1, 0);
  for (let r = 1; r <= n; r++) {
    const base = Math.floor(n / r), extra = n % r;
    const sizes = Array.from({ length: r }, (_, i) => base + (i < extra ? 1 : 0));
    let from = 0;
    if (sizes.every((c) => { const ok = rowWidth(from, c) <= available; from += c; return ok; })) return sizes;
  }
  return widths.map(() => 1);
}

export type PillLayout = { inline: boolean; rows: number[] };

/**
 * Measures the pills against the header row. `row` is the whole header row (Back, the chapter label, the pills);
 * `pills` is the pills' own container. Returns where the pills go and how they split.
 */
export function usePillLayout(row: RefObject<HTMLElement>, pills: RefObject<HTMLElement>, count: number): PillLayout {
  const [layout, setLayout] = useState<PillLayout>({ inline: true, rows: [count] });
  const measure = useCallback(() => {
    const r = row.current, p = pills.current;
    if (!r || !p) return;
    const pillEls = Array.from(p.querySelectorAll<HTMLElement>("[data-step-pill]"));
    const widths = pillEls.map((el) => el.getBoundingClientRect().width);
    if (widths.length === 0) return;
    const rowStyle = getComputedStyle(r);
    const colGap = parseFloat(rowStyle.columnGap) || 0;
    const full = r.clientWidth - (parseFloat(rowStyle.paddingLeft) || 0) - (parseFloat(rowStyle.paddingRight) || 0);
    // What Back, the Home link and the chapter label take on the first line.
    const lead = Array.from(r.children).filter((c) => c !== p)
      .reduce((sum, c) => sum + (c as HTMLElement).getBoundingClientRect().width + colGap, 0);
    const oneLine = widths.reduce((a, b) => a + b, 0) + PILL_GAP * (widths.length - 1);
    const next: PillLayout = oneLine <= full - lead + 0.5 ? { inline: true, rows: [widths.length] } : { inline: false, rows: balancedRows(widths, full) };
    setLayout((prev) => (prev.inline === next.inline && prev.rows.join() === next.rows.join() ? prev : next));
  }, [row, pills]);
  useLayoutEffect(() => {
    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (row.current) ro?.observe(row.current);
    return () => ro?.disconnect();
  }, [measure, count]);
  return layout;
}

/** Split `items` into rows of the given sizes. Pure. */
export function chunk<T>(items: readonly T[], sizes: readonly number[]): T[][] {
  const out: T[][] = [];
  let i = 0;
  for (const s of sizes) { out.push(items.slice(i, i + s)); i += s; }
  if (i < items.length) out.push(items.slice(i));
  return out;
}

export const usePillRefs = () => ({ row: useRef<HTMLDivElement>(null), pills: useRef<HTMLDivElement>(null) });
